import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Grid,
  Link,
  Theme,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Form, Formik, FormikProps } from "formik";
import * as yup from "yup";

import FormikFormField from "components/shared/FormikFormField";
import PasswordToggleIcon from "components/shared/PasswordToggleIcon";
import { Hero2Text } from "components/shared/Typography";
import { LOGIN } from "helpers/apis";
import useAxios from "helpers/axios";
import { ToastTypes } from "hooks/useApp";
import useAuth from "hooks/useAuth";
import useDialog from "hooks/useDialog";
import { IForm, errors } from "types/form";
import { IUser } from "types/user";

const useStyles = makeStyles((theme: Theme) => ({
  gridItem: {
    flex: "1 1 0",
    padding: "24px 0 32px",
    width: "100%",
  },
  linkP1: {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.body1.fontWeight,
    lineHeight: theme.typography.body1.lineHeight,
    letterSpacing: theme.typography.body1.letterSpacing,
    display: "block",
    marginTop: theme.spacing(2),
  },
}));

interface ILoginForm extends IForm {
  email: string;
  password: string;
}

const validationSchema = yup.object({
  email: yup.string().email(errors.invalidEmail).required(errors.required),
  password: yup.string().required(errors.required),
});

const LoginForm = React.memo(({ narrowMode }: { narrowMode: boolean }) => {
  const classes = useStyles();
  const [visiblePassword, setVisiblePassword] = useState<boolean>(false);
  const { setUser } = useAuth();
  const { openLoginDialog, setOpenLoginDialog, setOpenAccountRecoveryDialog } =
    useDialog();
  const history = useHistory();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const { fetchData, loading } = useAxios({
    config: { url: LOGIN, method: "POST" },
    callOnInit: false,
    successCallback: ({
      response,
      setToastMessage,
    }: {
      response: {
        data: Omit<IUser, "password"> & { token: string };
        message: string;
      };
      setToastMessage: Function;
    }) => {
      const { token, ...user } = response.data;
      localStorage.setItem("token", token);
      setUser(user);
      setToastMessage({
        message: response.message,
        type: ToastTypes.SUCCESS,
      });
      setOpenLoginDialog(false);
    },
  });

  const onSubmit = (values: ILoginForm) => {
    fetchData({ data: values });
  };

  const handleForgotPassword = useCallback(() => {
    if (openLoginDialog) {
      setOpenAccountRecoveryDialog(true);
      setOpenLoginDialog(false);
    } else {
      history.push("/forgot-password");
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [openLoginDialog]);

  return (
    <Formik
      initialValues={{
        email: "",
        password: "",
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {(props: FormikProps<ILoginForm>) => {
        const { dirty, isValid } = props;

        return (
          <>
            <Box mb={3}>
              <Hero2Text align={isMobile ? "center" : "left"}>
                Sign in
              </Hero2Text>
            </Box>
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormikFormField<ILoginForm>
                    name="email"
                    label="Email Address"
                    type="email"
                    {...props}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormikFormField
                    name="password"
                    type={visiblePassword ? "text" : "password"}
                    {...props}
                    textFieldProps={{
                      InputProps: {
                        endAdornment: (
                          <PasswordToggleIcon
                            visible={visiblePassword}
                            setVisible={setVisiblePassword}
                          />
                        ),
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    mt={1}
                    display="flex"
                    flexDirection="column"
                    alignItems={
                      openLoginDialog || isMobile ? "center" : "flex-start"
                    }
                  >
                    <Button
                      data-testid="signinButton"
                      variant="contained"
                      color="primary"
                      disabled={loading || !dirty || !isValid}
                      type="submit"
                      size="large"
                      fullWidth={isMobile}
                    >
                      Sign in
                    </Button>
                    <Link
                      data-testid="forgottenPasswordLink"
                      onClick={handleForgotPassword}
                      color="secondary"
                      underline="always"
                      className={classes.linkP1}
                    >
                      Forgot Your Password?
                    </Link>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </>
        );
      }}
    </Formik>
  );
});

const LoginComponent = () => {
  const classes = useStyles();
  const history = useHistory();
  const { openLoginDialog, setOpenLoginDialog, setOpenRegisterDialog } =
    useDialog();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const inNarrowMode: boolean =
    useMediaQuery((theme: Theme) => theme.breakpoints.down("md")) ||
    openLoginDialog;
  const handleReigster = useCallback(() => {
    if (openLoginDialog) {
      setOpenLoginDialog(false);
      setOpenRegisterDialog(true);
    } else {
      history.push("/register");
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [openLoginDialog, history]);

  return (
    <Grid
      container
      direction={inNarrowMode ? "column" : "row"}
      alignItems={inNarrowMode ? "center" : "flex-start"}
    >
      <Grid item component="section" className={classes.gridItem} xs={12}>
        <LoginForm narrowMode={inNarrowMode} />
      </Grid>
      <Box mx={inNarrowMode ? 0 : 4.5} alignSelf="stretch">
        <Divider orientation={inNarrowMode ? "horizontal" : "vertical"} />
      </Box>
      <Grid item component="section" className={classes.gridItem}>
        <Grid
          container
          direction="column"
          alignItems={openLoginDialog || isMobile ? "center" : "flex-start"}
        >
          <Box mb={3}>
            <Hero2Text align={inNarrowMode ? "center" : "inherit"}>
              New to Assay Marketplace
            </Hero2Text>
          </Box>
          <Grid item>
            <Button
              data-testid="registerButton"
              onClick={handleReigster}
              color="primary"
              variant="outlined"
              size="small"
              fullWidth={inNarrowMode}
            >
              Register for an account
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default LoginComponent;
